<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 500 : 'auto'"
    style="z-index: 1100 !important;"
  >
    <v-form
      @submit.prevent="submitForm"
      autocomplete="off"
      :disabled="isSubmitted"
      data-cy="dialog-purchaseAdditionalResources-form"
    >
      <v-card v-cloak data-cy="dialog-purchaseAdditionalResources-modal">

        <v-card-title class="d-flex justify-space-between mb-2" data-cy="dialog-purchaseAdditionalResources-title">
          {{ $t('routes.usage.purchaseAdditionalResource', { resource: resourceName }) }}

          <v-btn icon @click="closeDialog" :disabled="isSubmitted">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text scrollable class="scrollable">
          <!-- Current Usage and Limit -->
          <v-row>
            <v-col cols="12">
              <v-alert density="compact" type="info" variant="flat">
                <div class="d-flex justify-space-between">
                  <span>{{ $t('routes.usage.purchaseCurrentUsage') }}</span>
                  <span><strong>{{ formattedCurrentUsage }}</strong> / {{ formattedCurrentLimit }}</span>
                </div>
              </v-alert>
            </v-col>
          </v-row>

          <!-- Unit Selection -->
          <v-row>
            <v-col cols="12" sm="8" offset-sm="2">
              <v-number-input
                v-model="quantity"
                :label="$t('routes.usage.quantity')"
                :min="1"
                :rules="[quantityRules.required, quantityRules.positive, quantityRules.integer, quantityRules.max]"
                variant="outlined"
                density="compact"
                control-variant="hidden"
                data-cy="dialog-purchaseAdditionalResources-units"
              />
            </v-col>
          </v-row>

          <!-- Price Breakdown -->
          <v-row>
            <v-col cols="12" sm="8" offset-sm="2">
              <v-list density="compact">
                <v-list-item>
                  <template #prepend>
                    <v-icon>mdi-tag-outline</v-icon>
                  </template>
                  <v-list-item-title>
                    {{ this.priceLabel }}
                  </v-list-item-title>
                  <template #append>
                    <span>{{ formatCurrency(unitPrice) }}</span>
                  </template>
                </v-list-item>

                <v-divider />

                <v-list-item>
                  <template #prepend>
                    <v-icon>mdi-calculator</v-icon>
                  </template>
                  <v-list-item-title class="font-weight-bold">
                    {{ $t('routes.usage.purchaseTotalPrice') }}
                  </v-list-item-title>
                  <template #append>
                    <span class="font-weight-bold">{{ formatCurrency(totalPrice) }}</span>
                  </template>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>

          <!-- Billing Note -->
          <v-row>
            <v-col cols="12">
              <v-alert density="compact" type="warning" variant="outlined">
                {{ $t('routes.usage.purchaseBillingNote') }}
              </v-alert>
            </v-col>
          </v-row>

          <!-- Submission Error -->
          <div v-if="submissionError" class="bg-error px-2 py-1 mt-2 rounded">
            {{ submissionError }}
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            type="submit"
            :disabled="!isValidPurchase || isSubmitted"
            class="bg-primary"
            elevation="0"
            data-cy="dialog-purchaseAdditionalResources-submit"
          >
            {{ $t('routes.usage.purchase') }}
          </v-btn>

          <v-btn
            variant="text"
            @click="closeDialog"
            :disabled="isSubmitted"
            data-cy="dialog-purchaseAdditionalResources-cancel"
          >
            {{ $t('common.cancel') }}
          </v-btn>
        </v-card-actions>

      </v-card>
    </v-form>
  </v-dialog>
</template>

<script>
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import { useTenantStore } from '@/stores/TenantStore.js';
import { useAuthStore } from '@/stores/AuthStore.js';

const pricePerUnit = {
  'users': 25,
  'clientuser': 6,
  'storage': 50,
  'bandwidth': 25,
  'credits': 15,
};

const quantityPerUnit = {
  'users': 'x1',
  'clientuser': 'x1',
  'storage': '1 TB',
  'bandwidth': '100 GB',
  'credits': 'x1000',
};

const maxPurchaseQuantity = 1000;

export default {
  name: 'PurchaseAdditionalResourceDialog',
  mixins: [DialogActionKeys],
  props: {
    resourceType: {
      type: String,
    },
    currentUsage: {
      type: [Number, String],
      default: 0,
    },
    currentLimit: {
      type: [Number, String],
      default: 0,
    },
  },
  emits: ['resource-purchased'],
  data: function () {
    return {
      dialog: false,
      isSubmitted: false,
      submissionError: null,
      quantity: 0,
      quantityRules: {
        required: (v) => !!v || this.$t('routes.usage.purchaseQuantityRequired'),
        positive: (v) => v > 0 || this.$t('routes.usage.purchaseQuantityPositiveNumber'),
        integer: (v) => Number.isInteger(v) || this.$t('routes.usage.purchaseQuantityWholeNumber'),
        max: (v) => v <= maxPurchaseQuantity || this.$t('routes.usage.purchaseQuantityMaxValue', maxPurchaseQuantity),
      },
    };
  },
  watch: {
    dialog(val) {
      if (!val) {
        this.isSubmitted = false;
        this.submissionError = null;
        this.quantity = 0;
      }
    }
  },
  computed: {
    resourceName() {
      return {
        'users': this.$t('routes.usage.users'),
        'clientuser': this.$t('routes.usage.clientuser'),
        'credits': this.$t('routes.usage.credits'),
        'storage': this.$t('routes.usage.storage'),
        'bandwidth': this.$t('routes.usage.bandwidth'),
      }[this.resourceType]?.toLowerCase() ?? 'resource';
    },
    unitPrice() {
      return pricePerUnit[this.resourceType] ?? 0;
    },
    totalPrice() {
      return this.quantity * this.unitPrice;
    },
    isValidPurchase() {
      return this.quantity > 0 && this.quantity <= maxPurchaseQuantity;
    },
    formattedCurrentUsage() {
      return this.formatResourceValue(this.currentUsage);
    },
    formattedCurrentLimit() {
      return this.formatResourceValue(this.currentLimit);
    },
    priceLabel() {
      if (this.resourceType === 'bandwidth' || this.resourceType === 'credits') {
        return this.$t('routes.usage.purchaseOneOff', { quantity: quantityPerUnit[this.resourceType] });
      }

      return this.$t('routes.usage.purchasePricePerMonth', { quantity: quantityPerUnit[this.resourceType] });
    }
  },
  methods: {
    async submitForm() {
      this.isSubmitted = true;
      this.submissionError = null;

      const tenantStore = useTenantStore();
      const authStore = useAuthStore();

      try {
        await tenantStore.purchaseResources({
          accessPointId: authStore.getActiveAccesspoint.node.id,
          resource: this.resourceType,
          quantity: this.quantity,
        });

        this.$emit('resource-purchased', this.resourceType, this.quantity);
        this.closeDialog();
      } catch (error) {
        console.error(error);

        if (error?.response?.data?.error) {
          this.submissionError = this.$localizeMessage(error.response.data.error);
        } else {
          this.submissionError = this.$t('backend.system-error');
        }

        this.isSubmitted = false;
      }
    },
    closeDialog() {
      this.dialog = false;
    },
    openDialog() {
      this.dialog = true;
    },
    formatCurrency(value) {
      return `€${value.toFixed(2)}`;
    },
    formatResourceValue(value) {
      if (['bandwidth', 'storage'].includes(this.resourceType)) {
        const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB'];
        let i = 0;
        let size = Number(value);
        while (size >= 1000 && i < units.length - 1) {
          size /= 1000;
          i++;
        }
        return `${Math.round(size)} ${units[i]}`;
      }

      return value.toString();
    },
  }
};
</script>

<style scoped>

</style>
